import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './presentation/App';
import { buildServices } from './infrastructure/services';
import { EnvironmentError } from './infrastructure/firebase/env';
import './presentation/design-system/global.css';

/**
 * Application entry point.
 *
 * The service graph is built *before* React renders, so a misconfigured environment
 * produces one readable message on a blank page rather than a cascade of failures
 * from inside a component tree. A missing `VITE_FIREBASE_PROJECT_ID` should say so,
 * not surface twenty minutes later as an opaque Firestore error.
 */

const mount = (): void => {
  const container = document.getElementById('root');
  if (container === null) {
    throw new Error('No se encontró #root en index.html.');
  }

  try {
    buildServices();
  } catch (cause) {
    renderStartupError(container, cause);
    return;
  }

  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
};

/**
 * The one place the app renders without React.
 *
 * If `buildServices()` failed there is no Firebase, so there is no design system
 * worth booting and no session to show. Inline styles are deliberate here: this has
 * to work even if the stylesheet did not load.
 */
const renderStartupError = (container: HTMLElement, cause: unknown): void => {
  const isEnvironment = cause instanceof EnvironmentError;
  const message =
    cause instanceof Error ? cause.message : 'Ocurrió un error desconocido al arrancar.';

  container.innerHTML = '';

  const wrapper = document.createElement('div');
  wrapper.style.cssText = [
    'min-height:100vh',
    'display:grid',
    'place-items:center',
    'padding:40px',
    'background:#F4F6FA',
    "font-family:'Plus Jakarta Sans',system-ui,sans-serif",
    'color:#1B2431',
  ].join(';');

  const card = document.createElement('div');
  card.style.cssText = [
    'max-width:560px',
    'background:#fff',
    'border-radius:20px',
    'padding:32px',
    'box-shadow:0 1px 3px rgba(27,36,49,.06), 0 14px 32px -26px rgba(27,36,49,.5)',
  ].join(';');

  const title = document.createElement('h1');
  title.textContent = isEnvironment
    ? 'Falta configurar Firebase'
    : 'El portal no pudo arrancar';
  title.style.cssText = 'margin:0 0 12px;font-size:22px;font-weight:800;letter-spacing:-.02em';

  const body = document.createElement('p');
  body.textContent = message;
  body.style.cssText = 'margin:0;font-size:14px;line-height:1.6;color:#4B5A6E';

  card.append(title, body);

  if (isEnvironment) {
    const hint = document.createElement('p');
    hint.textContent = 'Los pasos completos están en docs/FIREBASE_SETUP.md.';
    hint.style.cssText = 'margin:16px 0 0;font-size:12.5px;color:#5C6A7D';
    card.append(hint);
  }

  wrapper.append(card);
  container.append(wrapper);

  console.error('[startup]', cause);
};

mount();
